import React from 'react';
import { hydrateRoot, createRoot } from 'react-dom/client';
import './index.css';
import './i18n/config'; // Initialize i18n before app renders
import App from './App';
import { AuthProvider } from './context/AuthContext';
import reportWebVitals from './reportWebVitals';

// Defensive: unregister any service worker from a prior CRA build and
// clear caches. Addendum Risk 3: returning visitors who had a SW
// installed before the prerender migration would otherwise get stale
// HTML served by their cached SW, causing hydration mismatches on
// every visit.
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker
    .getRegistrations()
    .then((regs) => regs.forEach((reg) => reg.unregister()))
    .catch(() => {});
}
if (typeof caches !== 'undefined') {
  caches
    .keys()
    .then((keys) => keys.forEach((k) => caches.delete(k)))
    .catch(() => {});
}

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Failed to find the root element');
}

const tree = (
  <React.StrictMode>
    <AuthProvider>
      <App />
    </AuthProvider>
  </React.StrictMode>
);

// Addendum Risk 2: surface every hydration mismatch. Without
// onRecoverableError, mismatches are silently swallowed and we only
// hear about them via user complaints. Wire to Sentry once available.
const onRecoverableError = (error: unknown, info: { componentStack?: string; digest?: string }) => {
  // eslint-disable-next-line no-console
  console.error('[hydration]', {
    message: error instanceof Error ? error.message : String(error),
    digest: info?.digest,
    componentStack: info?.componentStack?.slice(0, 800),
  });
  const w = window as unknown as { __hydrationErrors?: number; __hydrationLast?: unknown };
  w.__hydrationErrors = (w.__hydrationErrors ?? 0) + 1;
  w.__hydrationLast = error;
};

// scripts/prerender.js stamps data-prerendered="true" on #root after
// capturing HTML. If present, hydrate (don't blow away the prerendered
// markup). If absent (dev server, escape-hatch build), do a fresh render.
const wasPrerendered = rootElement.hasAttribute('data-prerendered');

if (wasPrerendered) {
  hydrateRoot(rootElement, tree, { onRecoverableError });
} else {
  createRoot(rootElement).render(tree);
}

// Signal that the first render is committed so the prerender script
// (scripts/prerender.js) knows when to capture HTML.
requestAnimationFrame(() => {
  requestAnimationFrame(() => {
    (window as unknown as { __APP_READY__?: boolean }).__APP_READY__ = true;
  });
});

reportWebVitals();
